import React from 'react';
import { Link } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ActionItemKeys, CareType, NotificationResponse, PsychAppointmentBookingSource } from '@uplift/api';
import { findPsychiatryRoute } from '@uplift/constants';
import { trackSnoozeToDo, trackToDoCTA, useActionItem, useL10n } from '@uplift/hooks';
import { UserRole } from '@uplift/types';
import { Button, StackableCard, useAlert } from '@uplift/ui-v2';
import { getActionItemAssessmentText } from '@uplift/utils';
import { usePatientDetailsQuery } from '@uplift/queries';
import { useCurrentuser } from '@uplift/contexts';

import skySrc from '../../../../../../assets/images/sky.jpg';

const useSeePsychStyles = makeStyles()(theme => ({
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gridGap: theme.spacing(1),
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    }
  },
  background: {
    background: `url('${skySrc}') lightgray 50% / cover no-repeat`,
  },
  content: {
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3)
  },
  body: {
    paddingTop: theme.spacing(0.5),
  }
}));

export const useSeePsychiatristItem = () => {
  const me = useCurrentuser();
  const alert = useAlert();
  const { data: patientDetails, isLoading: isPatientDetailsLoading } = usePatientDetailsQuery(me.id);
  const seePsychItem = useActionItem(ActionItemKeys.CLIENT_SEE_PSYCHIATRIST);
  const { classes: seePsychClasses } = useSeePsychStyles();
  const l10n = useL10n();

  const source = React.useMemo(() => {
    if (seePsychItem.isInitial) {
      return PsychAppointmentBookingSource.clientUpliftStandard;
    }
    if (seePsychItem.assessments?.phq9 && seePsychItem.assessments?.gad7) {
      return PsychAppointmentBookingSource.clientTodoPhq9Gad7;
    }
    if (seePsychItem.assessments?.phq9) {
      return PsychAppointmentBookingSource.clientTodoPhq9;
    }
    if (seePsychItem.assessments?.gad7) {
      return PsychAppointmentBookingSource.clientTodoGad7;
    }

    return undefined;
  }, [ seePsychItem ]);

  const seePsychiatristActionItem = React.useMemo(() => {
    if (!seePsychItem.show) {
      return null;
    }

    if (seePsychItem.isLoading) {
      return null;
    }

    const handleMaybeLater = async () => {
      await seePsychItem.dismiss(NotificationResponse.later);
      trackSnoozeToDo({
        toDoType: ActionItemKeys.CLIENT_SEE_PSYCHIATRIST,
        userType: UserRole.patient
      });
    };

    const handleDontShowAgain = async () => {
      await seePsychItem.dismiss(NotificationResponse.dismiss);
      trackSnoozeToDo({
        toDoType: ActionItemKeys.CLIENT_SEE_PSYCHIATRIST,
        userType: UserRole.patient
      });
      alert.info(<span>You can always schedule a consultation<br />for medication management later.<br /><br />Talk to your therapist for a referral.</span>, { timeout: 10000 });
    };

    let actionItemText = '';

    if (seePsychItem.isInitial) {
      if (patientDetails?.onboardingCaretypeSelection === CareType.therapyAndPsychiatry) {
        actionItemText = l10n.dashboard.actionItems.seePsych.bothText;
      } else {
        actionItemText = l10n.dashboard.actionItems.seePsych.initialText;
      }
    } else {
      const phq9text = seePsychItem.assessments?.phq9
        ? getActionItemAssessmentText('phq9', seePsychItem.assessments.phq9, l10n)
        : null;

      const gad7text = seePsychItem.assessments?.gad7
        ? getActionItemAssessmentText('gad7', seePsychItem.assessments.gad7, l10n)
        : null;

      actionItemText = l10n.dashboard.actionItems.seePsych.text(phq9text, gad7text);
    }

    let actionItemTitle = '';

    if (seePsychItem.isInitial) {
      if (patientDetails?.onboardingCaretypeSelection === CareType.therapyAndPsychiatry) {
        actionItemTitle = l10n.dashboard.actionItems.seePsych.bothTitle;
      } else {
        actionItemTitle = l10n.dashboard.actionItems.seePsych.initialTitle;
      }
    } else {
      actionItemTitle = l10n.dashboard.actionItems.seePsych.title;
    }

    const handleTracking = () => {
      trackToDoCTA({
        toDoType: ActionItemKeys.CLIENT_SEE_PSYCHIATRIST,
        userType: UserRole.patient
      });
    };

    const dismissButton = patientDetails?.onboardingCaretypeSelection === CareType.therapyAndPsychiatry ? (
      <Button
        variant="outlined"
        fullWidth
        onClick={handleDontShowAgain}
      >
        {l10n.dashboard.actionItems.seePsych.dontShowAgain}
      </Button>
    ) : (
      <Button
        variant="outlined"
        fullWidth
        onClick={handleMaybeLater}
      >
        {l10n.dashboard.actionItems.seePsych.maybeLater}
      </Button>
    );

    return (
      <StackableCard className={seePsychClasses.background}>
        <Box className={seePsychClasses.content}>
          <Box fontWeight={600}>
            {actionItemTitle}
          </Box>
          <Typography variant="body2" color="textPrimary" className={seePsychClasses.body}>
            {actionItemText}
          </Typography>
          <Box className={seePsychClasses.actions}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              component={Link}
              to={findPsychiatryRoute.createLink(null, { source })}
              onClick={handleTracking}
            >
              {l10n.dashboard.actionItems.seePsych.schedule}
            </Button>
            {dismissButton}
          </Box>
        </Box>
      </StackableCard>
    );
  }, [ l10n, seePsychClasses.actions, seePsychClasses.background, seePsychClasses.content, seePsychItem, source, patientDetails ]);

  return {
    isLoading: seePsychItem.isLoading || seePsychItem.isDismissing || isPatientDetailsLoading,
    actionItem: seePsychiatristActionItem
  };
};
